import React from 'react'
import { useTranslation } from 'react-i18next'
import Avatar from '../../Avatar'
import styles from './AttendeeMeetingAvailableListModal.module.scss'
import { useAttendeesWithStatus } from './hooks/useAttendeesWithStatus'
import type { Attendee, BusyTimes } from './types'
import type { TimeRange } from './utils/SchedulingUtils'
import { getAttendeeStatusIcon } from './utils/attendeeStatus'

interface AttendeeMeetingAvailableListModalProps {
  attendees: Attendee[]
  busyTimes?: BusyTimes
  selectedTimeRange?: (TimeRange & { allday?: boolean }) | null
  selectedTimeRangeList?: (TimeRange & { allday?: boolean })[] | null
  ownerHasConflict?: boolean
  onClose: () => void
}

const AttendeeMeetingAvailableListModal = ({
  attendees,
  busyTimes = {},
  selectedTimeRange = null,
  selectedTimeRangeList = null,
  ownerHasConflict = false,
  onClose,
}: AttendeeMeetingAvailableListModalProps) => {
  const { t } = useTranslation()

  // Compute attendee status using custom hook
  const attendeesWithStatus = useAttendeesWithStatus(
    attendees,
    busyTimes,
    selectedTimeRange,
    selectedTimeRangeList,
    ownerHasConflict,
  )

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>{t('calendar.schedulingAssistant.meetingAvailable')}</h2>
      </div>
      <div className={styles.content}>
        {attendeesWithStatus.map((attendee) => {
          const email = attendee.email
          const busyData = busyTimes[email]
          const { statusIcon, statusAlt } = getAttendeeStatusIcon(
            attendee,
            selectedTimeRange,
            selectedTimeRangeList,
            busyData,
          )

          return (
            <div key={attendee.email} className={styles.listItem}>
              <div className={styles.avatar}>
                <Avatar name={attendee.name || attendee.email} email={attendee.email} skipLogo />
              </div>
              <div className={styles.infoWrapper}>
                <div className={styles.info}>
                  <div className={styles.name}>{attendee.name || attendee.email.split('@')[0]}</div>
                  <div className={styles.email}>{attendee.email}</div>
                </div>
                {statusIcon && (
                  <div className={styles.status}>
                    <div className={styles.statusIconWrapper} title={statusAlt}>
                      <img src={statusIcon} alt={statusAlt} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <div className={styles.footer}>
        <button className={styles.closeButton} onClick={onClose}>
          {t('common.button.close')}
        </button>
      </div>
    </div>
  )
}

export default AttendeeMeetingAvailableListModal
